import React, { useState, useEffect, useRef } from 'react';
import { Book, Plus, Trash2, Paperclip, X } from 'lucide-react';
import api from '../services/api';
import '../styles/AdminComponents.css';

function QuickRepliesManagement() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ shortcut: '', message: '', shared: true });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(null); // { name, percent, sizeMB }
  const fileInputRef = useRef(null);

  const load = async () => {
    try { setItems((await api.get('/quick-replies')).data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.shortcut.startsWith('/')) return alert('O atalho deve começar com /');
    if (!form.message.trim() && !file) return alert('Informe uma mensagem ou anexe um arquivo');
    try {
      const fd = new FormData();
      fd.append('shortcut', form.shortcut);
      fd.append('message', form.message);
      fd.append('shared', form.shared ? 'true' : 'false');
      if (file) fd.append('file', file);

      if (file) {
        setUploading({ name: file.name, percent: 0, sizeMB: (file.size / 1048576).toFixed(1) });
      } else {
        setUploading({ name: 'Salvando...', percent: 50, sizeMB: '–' });
      }

      await api.post('/quick-replies', fd, {
        timeout: 0,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const percent = Math.round((evt.loaded / evt.total) * 100);
          setUploading(p => p ? { ...p, percent } : p);
        }
      });
      setForm({ shortcut: '', message: '', shared: true });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Erro');
    } finally {
      setUploading(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await api.delete(`/quick-replies/${id}`);
    load();
  };

  const fileLabel = (q) => {
    if (!q.file_name) return null;
    const icon = q.media_type === 'image' ? '🖼️'
      : q.media_type === 'video' ? '🎬'
      : q.media_type === 'audio' ? '🎵' : '📎';
    return `${icon} ${q.file_name}`;
  };

  return (
    <div className="admin-page">
      <h2><Book size={20}/> Respostas Rápidas</h2>
      <p className="muted">Digite /atalho no composer para enviar. Pode anexar arquivo ao atalho.</p>

      <div className="card">
        <h3>Nova resposta</h3>
        <label>Atalho (ex: /catalogo)</label>
        <input value={form.shortcut} onChange={e => setForm({ ...form, shortcut: e.target.value })} placeholder="/catalogo"/>
        <label>Mensagem (opcional se anexar arquivo — vira legenda)</label>
        <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4}/>

        <label>Arquivo (opcional)</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ flex: 1 }}
            accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.csv"
          />
          {file && (
            <button type="button"
              onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              className="btn-danger"
              style={{ padding: '4px 10px' }}>
              <X size={13}/> Remover
            </button>
          )}
        </div>
        {file && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
            {file.name} — {(file.size / 1024 / 1024).toFixed(1)} MB
          </p>
        )}

        <label style={{ marginTop: 12, display: 'block' }}>
          <input type="checkbox" checked={form.shared} onChange={e => setForm({ ...form, shared: e.target.checked })}/>
          {' '}Compartilhar com toda equipe
        </label>
        <button onClick={create} className="primary" disabled={!!uploading}>
          <Plus size={14}/> {uploading ? 'Enviando...' : 'Adicionar'}
        </button>

        {uploading && (
          <div className="qr-upload-progress">
            <div className="qr-upload-info">
              <span className="qr-upload-name">📤 {uploading.name}</span>
              <span className="qr-upload-percent">
                {uploading.percent < 100 ? `${uploading.percent}%` : 'Processando...'}
                {uploading.sizeMB && uploading.sizeMB !== '–' && ` · ${uploading.sizeMB} MB`}
              </span>
            </div>
            <div className="qr-upload-bar">
              <div className="qr-upload-fill" style={{ width: `${uploading.percent}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Desktop */}
      <div style={{overflowX:'auto', marginTop:12}}>
        <table className="admin-table qr-table-desktop">
          <thead><tr><th>Atalho</th><th>Mensagem</th><th>Arquivo</th><th>Compartilhada</th><th></th></tr></thead>
          <tbody>
            {items.map(q => (
              <tr key={q.id}>
                <td><code>{q.shortcut}</code></td>
                <td style={{maxWidth:300, wordBreak:'break-word', whiteSpace:'normal'}}>{q.message || <em style={{color:'var(--text-muted)'}}>—</em>}</td>
                <td style={{fontSize:12}}>{fileLabel(q) || <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                <td>{q.shared ? 'Sim' : 'Não'}</td>
                <td><button onClick={() => remove(q.id)} className="btn-danger"><Trash2 size={14}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="qr-cards">
        {items.map(q => (
          <div key={q.id} className="qr-card">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8}}>
              <code style={{fontSize:13, color:'var(--primary)', fontWeight:600}}>{q.shortcut}</code>
              <button onClick={() => remove(q.id)} className="btn-danger" style={{padding:'4px 8px', flexShrink:0}}><Trash2 size={13}/></button>
            </div>
            {q.message && <p style={{fontSize:13, color:'var(--text-secondary)', margin:'6px 0 4px', wordBreak:'break-word'}}>{q.message}</p>}
            {fileLabel(q) && <p style={{fontSize:12, color:'var(--text-muted)', margin:'4px 0'}}><Paperclip size={11} style={{verticalAlign:'middle'}}/> {fileLabel(q)}</p>}
            <span style={{fontSize:11, color:'var(--text-muted)'}}>{q.shared ? '🔗 Compartilhada' : '🔒 Pessoal'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default QuickRepliesManagement;
